/* iter55h — Character archetype catalogue (93 entries).
 *
 * Each entry: id (stable API slug), name, group, blurb (~1 sentence),
 * example (anchor), and optional traits (full definition used in the
 * hover-tooltip inside ArchetypePickerDialog).
 *
 * "Most Common" group: canonical literary archetypes with full trait
 * definitions supplied by the user (Feb 2026). Sits at the top of the
 * picker so writers can orient quickly.
 */

export const ARCHETYPE_GROUPS = [
  'Most Common — Hero / Heroine',
  'Most Common — Villain / Foil',
  'Core Jungian & Pearson',
  "Hero's Journey & Story Role",
  'Villain, Foil & Dark Reflection',
  'Gender & Relationship',
  'Social & Outsider',
  'Situational (The Heroine\'s Journey)',
];

export const ARCHETYPES = [
  /* ── Most Common: Hero / Heroine ───────────────────────────────── */
  { id: 'mc-epic-hero', name: 'The Epic Hero',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Superhuman strength, courage, and a clear moral code — their quest determines the fate of a world.',
    example: 'Achilles, Odysseus, Beowulf, Superman.',
    traits: 'Possesses superhuman strength, courage, and a clear moral code. Often of noble birth or divine lineage. Their journey is a grand quest that determines the fate of a nation or the world.' },

  { id: 'mc-everyman-hero', name: 'The Everyman / Reluctant Hero',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Ordinary person, extraordinary circumstances. Heroism comes from perseverance, not powers.',
    example: 'Frodo Baggins, Bilbo Baggins, John McClane, Katniss Everdeen.',
    traits: 'An ordinary person thrust into extraordinary circumstances. Relatable, grounded, and often unwilling at first. Their heroism comes from perseverance and a strong moral compass rather than special powers.' },

  { id: 'mc-chosen-one', name: 'The Chosen One',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Destined by prophecy or birthright. Struggles with the weight of fate but is the only one who can save the world.',
    example: 'Harry Potter, Neo (The Matrix), Luke Skywalker.',
    traits: 'Destined by prophecy or birthright to fulfill a great purpose. They often struggle with the weight of their destiny but are ultimately the only one who can save the world.' },

  { id: 'mc-classical-hero', name: 'The Classical Hero',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Noble but flawed. Brought low by hamartia — typically hubris — in the classical tragic pattern.',
    example: 'Oedipus, Hamlet, Macbeth, Othello.',
    traits: 'A hero of noble character who is flawed. Their downfall is often caused by a tragic flaw (hamartia), such as hubris (excessive pride). Admirable but not perfect.' },

  { id: 'mc-anti-hero', name: 'The Anti-Hero',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Lacks conventional heroic qualities — cynical, selfish, morally grey — yet the reader still roots for them.',
    example: 'Deadpool, Walter White, Severus Snape, Han Solo.',
    traits: 'A protagonist who lacks conventional heroic qualities. May be cynical, selfish, or morally ambiguous, but the audience still roots for them. Operates in a grey area between good and evil.' },

  { id: 'mc-tragic-hero', name: 'The Tragic Hero',
    group: 'Most Common — Hero / Heroine',
    blurb: 'High-status character whose fatal flaw or conflict with fate leads to destruction — and ultimately, self-awareness.',
    example: 'Othello, King Lear, Anakin Skywalker / Darth Vader.',
    traits: 'A character of high status who suffers a downfall due to a fatal flaw or a conflict with a superior force (like fate). Their story typically ends in their destruction, but not before they gain some self-awareness.' },

  { id: 'mc-warrior-knight', name: 'The Warrior / The Knight',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Defined by skill in combat and a code of honour — loyal, brave, and fiercely protective.',
    example: 'Sir Lancelot, Wonder Woman, Aragorn, Captain America.',
    traits: 'A hero defined by their skill in combat and their adherence to a code of honour (e.g. chivalry). Loyal, brave, and protective.' },

  { id: 'mc-scholar-sage', name: 'The Scholar / The Sage',
    group: 'Most Common — Hero / Heroine',
    blurb: 'Intellect and wisdom over brute force. Often a mentor or strategist who changes the outcome through knowledge.',
    example: 'Hermione Granger, Gandalf, Obi-Wan Kenobi, Sherlock Holmes.',
    traits: 'A hero who uses intellect, wisdom, and knowledge to overcome obstacles rather than brute force. Often a mentor or strategist.' },

  /* ── Most Common: Villain / Foil ────────────────────────────────── */
  { id: 'mc-arch-nemesis', name: 'The Arch-Nemesis',
    group: 'Most Common — Villain / Foil',
    blurb: "The hero's primary and most powerful antagonist. Their conflict is deeply personal and defines both characters.",
    example: 'The Joker (Batman), Professor Moriarty, Voldemort, Darth Vader.',
    traits: "The hero's primary and most powerful antagonist. Their goals and abilities directly oppose the hero's. Their conflict is deeply personal and often defines both characters." },

  { id: 'mc-foil', name: 'The Foil',
    group: 'Most Common — Villain / Foil',
    blurb: 'Contrasts the protagonist to sharpen their defining qualities — not always a villain, but always a mirror.',
    example: 'Draco Malfoy, Gaston, Mercutio.',
    traits: "A character whose purpose is to contrast with the protagonist to highlight the protagonist's specific qualities. A foil is not always a villain, but a villain can be a foil." },

  { id: 'mc-mastermind', name: 'The Mastermind',
    group: 'Most Common — Villain / Foil',
    blurb: 'Operates from the shadows, calm and calculating — always several steps ahead of everyone else in the room.',
    example: 'Lex Luthor, Palpatine, Professor Moriarty.',
    traits: 'A villain who operates from the shadows, using intellect and manipulation to achieve their goals. Calm, calculating, and always several steps ahead.' },

  { id: 'mc-dark-mirror', name: 'The Dark Mirror / Shadow',
    group: 'Most Common — Villain / Foil',
    blurb: 'Represents what the hero could become. A physical manifestation of their darkest impulses or unchosen path.',
    example: 'Venom (Spider-Man), Two-Face, Darth Vader vs. Luke.',
    traits: "A villain who represents what the hero could become if they gave in to their darkest impulses or made different choices. A physical manifestation of the hero's internal struggle." },

  { id: 'mc-brute-enforcer', name: 'The Brute / The Enforcer',
    group: 'Most Common — Villain / Foil',
    blurb: 'Relies on physical strength and intimidation. The primary physical threat — usually muscle for a smarter villain.',
    example: 'The Mountain (Game of Thrones), Bane (The Dark Knight Rises).',
    traits: 'A villain who relies on physical strength and intimidation. Often the primary physical threat to the hero but usually subservient to a more intelligent "mastermind" villain.' },

  { id: 'mc-corrupted-hero', name: 'The Corrupted / Fallen Hero',
    group: 'Most Common — Villain / Foil',
    blurb: 'Was once a hero. Turned by tragedy, betrayal, or hunger for power — a tragic warning to the protagonist.',
    example: 'Harvey Dent / Two-Face, Anakin / Darth Vader, Saruman.',
    traits: 'A character who was once a hero but has turned to evil due to tragedy, betrayal, or a desire for power. Often serves as a tragic warning to the protagonist.' },

  { id: 'mc-tempter-seducer', name: 'The Tempter / The Seducer',
    group: 'Most Common — Villain / Foil',
    blurb: 'Uses charm, manipulation, and offers of pleasure or power to lure the hero off their righteous path.',
    example: 'The White Witch (Narnia), The Serpent (Genesis), Saruman\'s voice.',
    traits: "A villain who uses charm, manipulation, and offers of power or pleasure to lure the hero off their path. Preys on the hero's desires and weaknesses." },

  { id: 'mc-force-of-nature', name: 'The Force of Nature',
    group: 'Most Common — Villain / Foil',
    blurb: 'Not a person but an unstoppable entity or system. Cannot be reasoned with. Represents an apocalyptic threat.',
    example: 'The Borg (Star Trek), Sauron, The Nothing (The Neverending Story).',
    traits: 'A villain that is not a person but an unstoppable force, entity, or system. Cannot be reasoned with and represents a fundamental, often apocalyptic, threat.' },


  /* ── Core Jungian & Pearson ──────────────────────────────────────── */
  { id: 'jungian-innocent', name: 'The Innocent',
    group: 'Core Jungian & Pearson',
    blurb: 'Seeks safety, happiness, and simplicity. Optimistic and trusting, but can be naive.',
    traits: 'Seeks safety, happiness, and simplicity. Optimistic and trusting, but can be naive or in denial of problems.' },

  { id: 'jungian-orphan', name: 'The Orphan / Everyman',
    group: 'Core Jungian & Pearson',
    blurb: 'Seeks belonging and connection. Down-to-earth and empathetic, but may fear abandonment.',
    traits: 'Seeks belonging and connection. Down-to-earth and empathetic, but may fear standing out or being abandoned.' },

  { id: 'jungian-hero-warrior', name: 'The Hero / Warrior',
    group: 'Core Jungian & Pearson',
    blurb: 'Seeks mastery and courage through action, but can become arrogant or see everything as a battle.',
    traits: 'Seeks mastery and courage. Proves worth through challenges and action, but can be arrogant or see everything as a battle.' },

  { id: 'jungian-caregiver', name: 'The Caregiver',
    group: 'Core Jungian & Pearson',
    blurb: 'Compassionate and nurturing, but can become a martyr or enable the dependence of others.',
    traits: 'Seeks to protect and serve others. Compassionate and nurturing, but can become a martyr or enable dependence.' },

  { id: 'jungian-explorer', name: 'The Explorer / Seeker',
    group: 'Core Jungian & Pearson',
    blurb: 'Restless and authentic, driven by freedom and discovery, but struggles to commit.',
    traits: 'Seeks freedom and discovery. Restless and authentic, but can be unable to commit or always look for greener grass.' },

  { id: 'jungian-lover', name: 'The Lover',
    group: 'Core Jungian & Pearson',
    blurb: 'Passionate and committed, but risks losing their own identity in relationships.',
    traits: 'Seeks intimacy and pleasure. Passionate and committed, but can lose their own identity in relationships.' },

  { id: 'jungian-destroyer', name: 'The Destroyer / Rebel / Outlaw',
    group: 'Core Jungian & Pearson',
    blurb: 'Bold and catalytic, seeks liberation and revolution, but can be destructive or motivated by pain.',
    traits: 'Seeks liberation and revolution. Bold and catalytic, but can be destructive or motivated by unresolved pain.' },

  { id: 'jungian-creator', name: 'The Creator',
    group: 'Core Jungian & Pearson',
    blurb: 'Imaginative and driven by self-expression, but can be perfectionistic or struggle to finish.',
    traits: 'Seeks innovation and self-expression. Imaginative and driven, but can be perfectionistic or struggle to finish.' },

  { id: 'jungian-ruler', name: 'The Ruler',
    group: 'Core Jungian & Pearson',
    blurb: 'Confident and strategic, but may become authoritarian or emotionally cold in the pursuit of order.',
    traits: 'Seeks order and control. Confident and strategic, but can become authoritarian or emotionally cold.' },

  { id: 'jungian-magician', name: 'The Magician / Shaman',
    group: 'Core Jungian & Pearson',
    blurb: 'Visionary and knowledgeable of hidden things, but can be manipulative or fear their own power.',
    traits: 'Seeks transformation and power. Visionary and knowledgeable of hidden things, but can be manipulative or fear their own power.' },

  { id: 'jungian-sage', name: 'The Sage',
    group: 'Core Jungian & Pearson',
    blurb: 'Wise and analytical, seeks truth, but can be detached or use knowledge as an excuse for inaction.',
    traits: 'Seeks truth and understanding. Wise and analytical, but can be detached or use knowledge as an excuse for inaction.' },

  { id: 'jungian-jester', name: 'The Jester / Fool / Trickster',
    group: 'Core Jungian & Pearson',
    blurb: 'Playful and boundary-breaking, seeks enjoyment and lightness, but can be cruel or avoid real issues.',
    traits: "Seeks enjoyment and lightness. Playful and boundary-breaking, but can be cruel or avoid real issues." },

  /* ── Hero's Journey & Story Role ────────────────────────────────── */
  { id: 'hj-mentor', name: 'The Mentor',
    group: "Hero's Journey & Story Role",
    blurb: 'Provides guidance, gifts, and wisdom to the hero, often acting as a conscience or parent figure.',
    traits: 'Provides guidance, gifts, and wisdom to the hero, often acting as a conscience or parent figure.' },

  { id: 'hj-herald', name: 'The Herald',
    group: "Hero's Journey & Story Role",
    blurb: 'Announces a change or call to adventure. Sets the story in motion and provides information.',
    traits: 'Announces a change or call to adventure. They set the story in motion and provide information.' },

  { id: 'hj-threshold-guardian', name: 'The Threshold Guardian',
    group: "Hero's Journey & Story Role",
    blurb: "Tests the hero's resolve and readiness at key points in the journey, standing in the way of progress.",
    traits: "Tests the hero's resolve and readiness at key points in the journey, standing in the way of progress." },

  { id: 'hj-shapeshifter', name: 'The Shapeshifter',
    group: "Hero's Journey & Story Role",
    blurb: 'Creates suspense by being two-faced or unpredictable. Loyalty is uncertain, often linked to the Anima/Animus.',
    traits: 'Creates suspense by being two-faced or unpredictable. Their loyalty is uncertain, often linked to the Anima/Animus.' },

  { id: 'hj-shadow', name: 'The Shadow',
    group: "Hero's Journey & Story Role",
    blurb: "The primary antagonist or the hero's dark side. Represents repressed fears or a worthy opponent.",
    traits: "The primary antagonist or the hero's dark side. Represents the hero's repressed fears or a worthy opponent to be destroyed." },

  { id: 'hj-ally', name: 'The Ally',
    group: "Hero's Journey & Story Role",
    blurb: 'A loyal companion who supports the hero but never does the work for them.',
    traits: 'A loyal companion who supports the hero but never does the work for them.' },

  { id: 'hj-friendly-beast', name: 'The Friendly Beast',
    group: "Hero's Journey & Story Role",
    blurb: "An animal companion that shows nature is on the hero's side.",
    traits: "An animal companion that shows nature is on the hero's side." },

  /* ── Villain, Foil & Dark Reflection ────────────────────────────── */
  { id: 'v-arch-nemesis', name: 'The Arch-Nemesis',
    group: 'Villain, Foil & Dark Reflection',
    blurb: "The hero's ultimate rival. Goals directly oppose the hero's, often through deeply personal conflict.",
    traits: "The hero's ultimate rival, whose goals directly oppose the hero's. Often defined by a deeply personal conflict." },

  { id: 'v-dark-mirror', name: 'The Dark Mirror / Doppelganger',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'Represents what the hero could become. Forces them to confront their own dark potential.',
    traits: 'A villain who represents what the hero could become. They force the hero to confront their own dark potential.' },

  { id: 'v-corrupted-hero', name: 'The Corrupted / Fallen Hero',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'A once-good character who turned to evil — a tragic warning of what the hero could become.',
    traits: 'A once-good character who turned to evil. They serve as a tragic warning of the hero\'s potential fate.' },

  { id: 'v-mastermind', name: 'The Mastermind',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'Operates through intellect and manipulation, remaining several steps ahead of everyone else.',
    traits: 'A villain who operates through intellect and manipulation, remaining steps ahead of everyone else.' },

  { id: 'v-brute', name: 'The Brute / Enforcer',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'Physically imposing and the immediate physical threat — often subservient to a smarter boss.',
    traits: 'A physically imposing villain who serves as the immediate physical threat, often subservient to a smarter boss.' },

  { id: 'v-devil', name: 'The Devil Figure',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'Evil incarnate, with no redeeming qualities. Represents pure antagonistic force.',
    traits: 'Evil incarnate, with no redeeming qualities. They represent pure antagonistic force.' },

  { id: 'v-redeemable-villain', name: 'The Evil Figure with the Good Heart',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'A villain who is saved or redeemed, often by love. Their arc is the story\'s emotional centre.',
    traits: 'A villain who is saved or redeemed, often by love.' },

  { id: 'v-nightmare-creature', name: 'The Creature of Nightmare',
    group: 'Villain, Foil & Dark Reflection',
    blurb: 'A monster from the deepest psyche. Often a desecration of the human form.',
    traits: 'A monster from the deepest psyche, often a desecration of the human form.' },

  /* ── Gender & Relationship ───────────────────────────────────────── */
  { id: 'gr-femme-fatale', name: 'The Femme Fatale',
    group: 'Gender & Relationship',
    blurb: 'A seductive, charming woman who uses her allure to control and destroy.',
    traits: 'A seductive, charming woman who uses her allure to control and destroy men.' },

  { id: 'gr-maiden', name: 'The Maiden / Damsel in Distress',
    group: 'Gender & Relationship',
    blurb: 'An innocent figure who needs rescuing — representing purity, a prize, or sometimes a trap.',
    traits: 'An innocent figure who needs rescuing. She often represents purity or a prize, but can also be a trap.' },

  { id: 'gr-great-mother', name: 'The Great Mother / Mother Creator',
    group: 'Gender & Relationship',
    blurb: 'A nurturing, life-giving figure — sometimes idealized and all-powerful.',
    example: 'Gaia.',
    traits: 'A nurturing, life-giving figure, sometimes idealized and all-powerful (e.g. Gaia).' },

  { id: 'gr-anima', name: 'The Anima / Animus',
    group: 'Gender & Relationship',
    blurb: 'The inner feminine in a man / inner masculine in a woman. Often a guide from the unconscious.',
    traits: 'The inner feminine in a man / inner masculine in a woman. Often a guide or messenger from the unconscious.' },

  { id: 'gr-wise-old-man', name: 'The Wise Old Man',
    group: 'Gender & Relationship',
    blurb: 'A masculine figure of wisdom and guidance, similar to the Mentor archetype.',
    traits: 'A masculine figure of wisdom and guidance, similar to the Mentor.' },

  { id: 'gr-platonic-ideal', name: 'The Platonic Ideal',
    group: 'Gender & Relationship',
    blurb: 'A woman who serves as intellectual, rather than physical, inspiration for the hero.',
    traits: 'A woman who serves as an intellectual, rather than physical, inspiration for the hero.' },

  /* ── Social & Outsider ───────────────────────────────────────────── */
  { id: 'so-scapegoat', name: 'The Scapegoat',
    group: 'Social & Outsider',
    blurb: 'Banished or killed to expiate the sins of a community. Their death has more power than their life.',
    traits: 'A character banished or killed to expiate the sins of a community. Their death often has more power than their life.' },

  { id: 'so-outcast', name: 'The Outcast / Loner',
    group: 'Social & Outsider',
    blurb: 'Isolated from society by choice or force. Possesses a unique, often critical perspective.',
    traits: 'A character isolated from society, either by choice or force. They possess a unique, often critical, perspective.' },

  { id: 'so-rebel', name: 'The Rebel / Revolutionary',
    group: 'Social & Outsider',
    blurb: 'Fights against an oppressive system, often at great personal cost.',
    traits: 'A character who fights against an oppressive system, often at great personal cost.' },

  { id: 'so-fool', name: 'The Fool / Jester',
    group: 'Social & Outsider',
    blurb: 'Uses humour to expose truth, cut down the powerful, or cope with tragedy.',
    traits: 'A figure who uses humor to expose truth, cut down the powerful, or cope with tragedy.' },

  /* ── Situational (The Heroine's Journey) ────────────────────────── */
  { id: 'sit-quest', name: 'The Quest',
    group: "Situational (The Heroine's Journey)",
    blurb: 'The search for a person, place, or object that will restore balance to the world.',
    traits: 'The search for a person, place, or object that will restore balance to the world.' },

  { id: 'sit-task', name: 'The Task',
    group: "Situational (The Heroine's Journey)",
    blurb: 'A nearly impossible feat the hero must perform to complete their quest.',
    traits: 'A nearly impossible feat the hero must perform to complete their quest.' },

  { id: 'sit-journey', name: 'The Journey',
    group: "Situational (The Heroine's Journey)",
    blurb: 'A voyage in search of truth that helps save a kingdom or a soul.',
    traits: 'A voyage in search of truth that helps save a kingdom or a soul.' },

  { id: 'sit-initiation', name: 'The Initiation',
    group: "Situational (The Heroine's Journey)",
    blurb: "The adolescent's coming of age and entry into maturity with new awareness.",
    traits: "The adolescent's coming of age and entry into maturity with new awareness." },

  { id: 'sit-fall', name: 'The Fall',
    group: "Situational (The Heroine's Journey)",
    blurb: 'A descent from a higher state due to transgression, resulting in the loss of innocence.',
    traits: 'A descent from a higher state, often due to a transgression, resulting in the loss of innocence.' },

  { id: 'sit-death-rebirth', name: 'Death and Rebirth',
    group: "Situational (The Heroine's Journey)",
    blurb: 'A cyclical pattern paralleling nature — a character dies (literally or metaphorically) to be reborn.',
    traits: 'A cyclical pattern paralleling nature, where a character dies (literally or metaphorically) to be reborn.' },

  { id: 'sit-unhealable-wound', name: 'The Unhealable Wound',
    group: "Situational (The Heroine's Journey)",
    blurb: 'A physical or psychological wound that cannot fully heal — symbolising a permanent loss.',
    traits: 'A physical or psychological wound that cannot fully heal, symbolizing a permanent loss.' },
];

/** All slug IDs, dedup'd, matching the archetypes array order. */
export const ARCHETYPE_IDS = ARCHETYPES.map((a) => a.id);
